import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Eye, EyeOff, ShieldCheck, KeyRound } from 'lucide-react';
import LanguageSelector from '@/components/LanguageSelector';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [needs2FA, setNeeds2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const { login } = useAuth();
  const { loginWithRedirect } = useAuth0();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (needs2FA && !useRecovery && !totpCode) {
      toast.error('Enter your 2FA code');
      return;
    }
    if (needs2FA && useRecovery && !recoveryCode) {
      toast.error('Enter your recovery code');
      return;
    }

    setLoading(true);
    try {
      const result = await login(
        email,
        password,
        needs2FA && !useRecovery ? totpCode : null,
        needs2FA && useRecovery ? recoveryCode : null
      );
      if (result?.requires_2fa) {
        setNeeds2FA(true);
        toast.info('Enter your Google Authenticator code');
      } else {
        toast.success('Welcome back!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (connection) => {
    loginWithRedirect({
      authorizationParams: { connection },
      appState: { returnTo: '/dashboard' },
    });
  };

  return (
    <div className="min-h-screen bg-navy-950 flex items-center justify-center px-6 py-12">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(0,212,255,0.1)_0%,transparent_60%)]" />
      
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative"
      >
        <Link to="/" className="flex justify-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_7d9bc2e5-ff38-4e06-b101-9f0eb41c2616/artifacts/w350as9h_LOGOvrn-02.png" 
            alt="VerbNotes" 
            className="h-16"
          />
        </Link>

        <div className="glass rounded-2xl p-8">
          <h1 className="font-heading text-3xl text-white text-center mb-2">{t('login_title')}</h1>
          <p className="text-slate-400 text-center mb-6">{t('login_subtitle')}</p>

          {!needs2FA && (
            <>
              {/* Social Login Buttons */}
              <div className="space-y-3 mb-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleSocialLogin('google-oauth2')}
                  className="w-full bg-navy-900/50 border-white/10 text-white hover:bg-white/10 rounded-xl py-5 flex items-center justify-center gap-3"
                  data-testid="login-google-btn"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  Continue with Google
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleSocialLogin('github')}
                  className="w-full bg-navy-900/50 border-white/10 text-white hover:bg-white/10 rounded-xl py-5 flex items-center justify-center gap-3"
                  data-testid="login-github-btn"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/></svg>
                  Continue with GitHub
                </Button>

                <div className="grid grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleSocialLogin('facebook')}
                    className="bg-navy-900/50 border-white/10 text-white hover:bg-white/10 rounded-xl py-5 flex items-center justify-center gap-2"
                    data-testid="login-facebook-btn"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    Facebook
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleSocialLogin('apple')}
                    className="bg-navy-900/50 border-white/10 text-white hover:bg-white/10 rounded-xl py-5 flex items-center justify-center gap-2"
                    data-testid="login-apple-btn"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
                    Apple
                  </Button>
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-4 mb-6">
                <Separator className="flex-1 bg-white/10" />
                <span className="text-slate-500 text-sm">or</span>
                <Separator className="flex-1 bg-white/10" />
              </div>
            </>
          )}

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {!needs2FA && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">{t('login_email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500"
                    data-testid="login-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-300">{t('login_password')}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500 pr-10"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <div className="text-right">
                    <Link to="/forgot-password" className="text-cyan text-sm hover:underline" data-testid="forgot-password-link">
                      Forgot password?
                    </Link>
                  </div>
                </div>
              </>
            )}

            {needs2FA && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                <div className="flex items-center gap-2 p-3 rounded-xl bg-cyan/10 border border-cyan/20 mb-2">
                  <ShieldCheck className="text-cyan shrink-0" size={18} />
                  <span className="text-cyan text-sm font-medium">Two-factor authentication required</span>
                </div>

                {!useRecovery ? (
                  <div className="space-y-2">
                    <Label htmlFor="totp" className="text-slate-300 flex items-center gap-2">
                      <ShieldCheck size={16} />
                      Google Authenticator Code
                    </Label>
                    <Input
                      id="totp"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="000000"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500 text-center text-lg tracking-[0.3em]"
                      autoFocus
                      data-testid="login-2fa-input"
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="recovery" className="text-slate-300 flex items-center gap-2">
                      <KeyRound size={16} />
                      Recovery Code
                    </Label>
                    <Input
                      id="recovery"
                      type="text"
                      maxLength={9}
                      placeholder="XXXX-XXXX"
                      value={recoveryCode}
                      onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                      className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500 text-center text-lg tracking-[0.15em] uppercase"
                      autoFocus
                      data-testid="login-recovery-input"
                    />
                    <p className="text-slate-500 text-xs">Using a recovery code will disable 2FA. You can re-enable it from settings.</p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => { setUseRecovery(!useRecovery); setTotpCode(''); setRecoveryCode(''); }}
                  className="text-cyan text-sm hover:underline w-full text-center"
                  data-testid="toggle-recovery-btn"
                >
                  {useRecovery ? 'Use Authenticator code instead' : 'Lost access? Use a recovery code'}
                </button>
              </motion.div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-cyan text-navy-950 hover:bg-cyan-300 rounded-full py-6 font-semibold shadow-[0_0_20px_rgba(0,212,255,0.3)]"
              data-testid="login-submit-btn"
            >
              {loading ? t('login_signing') : needs2FA ? 'Verify & Sign In' : t('login_button')}
            </Button>
          </form>

          {needs2FA && (
            <button
              type="button"
              onClick={() => { setNeeds2FA(false); setTotpCode(''); setRecoveryCode(''); setUseRecovery(false); }}
              className="text-slate-400 text-sm hover:text-white w-full text-center mt-4"
              data-testid="back-to-login-btn"
            >
              Back to login
            </button>
          )}

          {!needs2FA && (
            <p className="text-center text-slate-400 mt-6">
              {t('login_no_account')}{' '}
              <Link to="/register" className="text-cyan hover:underline" data-testid="login-register-link">
                {t('login_create')}
              </Link>
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
}
